import { getServerRuntimeConfig } from '../../../lib/runtimeConfig';
import { Box } from '@mui/material';
import Bugsnag from '@bugsnag/js';
import Head from 'next/head';
import { type GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useI18nContext } from '@procore/core-react';
import Footer from '../../../components/Layout/Footer';
import GoogleBreadcrumbScript from '../../../components/Google/GoogleBreadcrumbScript';
import GoogleWebPageScript from '../../../components/Google/GoogleWebPageScript';
import Header from '../../../components/Layout/Header';
import SearchResults from '../../../components/Search/SearchResults';
import {
  type ProjectsSearchRequest,
  type SearchResultData,
  fetchSearch,
} from '../../../data/projectsSearch';
import { type SearchRequest as BusinessSearchRequest } from '../../../data/search';
import { absoluteUrl } from '../../../helpers/absolute-url';
import {
  type FilterSet,
  getInitialFilterState,
  getValidFiltersFromQuery,
} from '../../../helpers/filters';
import useFilters from '../../../helpers/hooks/useFilters';
import { getLocationBreadcrumbs } from '../../../helpers/location-breadcrumbs';
import { type GeoInfo, getGeoInfo } from '../../../services/geo-info';
import { type PaginationDataSet } from '../../../types/pagination';
import LocationError from '../../../components/Error/LocationError';
import { getStates, type TopStatesResult } from '../../../services/states';
import { DEFAULT_COUNTRY } from '../../../data/location/countries';
import { useStyles } from '../../search.styles';
import FrequentlyAskedQuestions from '../../../components/FAQ/FrequentlyAskedQuestions';
import { usePageView } from '../../../components/Analytics/utilities';
import {
  type FiltersLists,
  getFiltersLists,
} from '../../../services/business-registry';
import { type FaqData, generateFaqs } from '../../../helpers/generate-faqs';
import { setupI18next } from '../../../helpers/setup-i18next';
import {
  getProjectsInitialPagination,
  getSearchResults,
} from '../../../services/project-search';
import { getSearchResults as getBusinessSearchResults } from '../../../services/search';
import DistanceUnit from '../../../data/location/distance-units';
import {
  DEFAULT_SEARCH_RADIUS,
  DEFAULT_SEARCH_RADIUS_KM_TO_MI,
} from '../../../constants';

const serverRuntimeConfig = getServerRuntimeConfig();

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { req, locale } = context;
  const { origin } = absoluteUrl(req);

  const { location, q, page, pageSize } = context.query;
  const workingLocation = location?.toString();
  const workingQ = q?.toString();
  const initialPaginationData = getProjectsInitialPagination(
    page as string,
    pageSize as string
  );

  let geoInfo: GeoInfo = null;
  let topStates = null;
  if (workingLocation) {
    let geoInfoAndPolygons;
    try {
      geoInfoAndPolygons = await getGeoInfo(workingLocation);
    } catch (err) {
      Bugsnag.notify(err);
      return {
        props: {
          geoInfo,
          origin,
          initialFilters: null,
          dataError: true,
        },
      };
    }
    geoInfo = geoInfoAndPolygons?.geoInfo;

    if (!geoInfo?.country) {
      try {
        topStates = await getStates(DEFAULT_COUNTRY);
      } catch (err) {
        Bugsnag.notify(err);
      }
    }
  }

  const validFilters = await getValidFiltersFromQuery(
    context.query,
    context.locale,
    true
  );
  const initialFilters = getInitialFilterState(
    validFilters,
    !!geoInfo?.city,
    [],
    true
  );
  if (context.locale !== 'en-US') {
    initialFilters.radius.value = 15.53;
  }

  const searchRadius =
    context.locale === 'en-US'
      ? DEFAULT_SEARCH_RADIUS
      : DEFAULT_SEARCH_RADIUS_KM_TO_MI;
  const request: ProjectsSearchRequest = {
    businessRegistryUrl: serverRuntimeConfig.businessRegistry,
    geoInfo,
    sectors: validFilters.constructionSector?.map((f) => f.key) ?? [],
    trades: validFilters.trade?.map((f) => f.key) ?? [],
    classifications: validFilters.classifications?.map((f) => f.key) ?? [],
    query: workingQ,
    page: initialPaginationData.page,
    pageSize: initialPaginationData.pageSize,
    radius: searchRadius,
    unit: DistanceUnit.Mile,
    sort: 'last_updated',
    loggingContext: `Project Search Page: query: ${workingQ}, location: ${location}`,
  };
  const businessSearchRequest: BusinessSearchRequest = {
    businessRegistryUrl: serverRuntimeConfig.businessRegistry,
    geoInfo,
    sectors: [],
    types: [],
    trades: [],
    classifications: [],
    query: '',
    page: 0,
    pageSize: 0,
    radius: searchRadius,
    businessFloor: 0,
    unit: DistanceUnit.Mile,
    sort: 'relevance',
    loggingContext: `Project Search Page FAQ: location: ${location}`,
  };
  const [initialResults, businessResults] = await Promise.all([
    getSearchResults(request),
    getBusinessSearchResults(businessSearchRequest),
  ]);

  const I18n = setupI18next(context.locale);
  const faqs = generateFaqs(
    businessResults.count,
    null,
    null,
    initialFilters,
    geoInfo,
    I18n,
    locale
  );
  const filtersLists = await getFiltersLists(context.locale, {
    projectsSearch: true,
  });

  return {
    props: {
      geoInfo,
      origin,
      initialFilters,
      dataError: false,
      topStates,
      initialResults,
      faqs,
      filtersLists,
      initialPaginationData,
    },
  };
}

export default function Results({
  geoInfo,
  origin,
  initialFilters,
  dataError,
  topStates,
  initialResults,
  faqs,
  filtersLists,
  initialPaginationData,
}: {
  geoInfo: GeoInfo;
  origin: string;
  initialFilters: FilterSet;
  dataError: boolean;
  topStates: TopStatesResult[];
  initialResults: SearchResultData;
  faqs: FaqData[];
  filtersLists: FiltersLists;
  initialPaginationData: PaginationDataSet;
}) {
  const classes = useStyles();
  const router = useRouter();
  const I18n = useI18nContext();
  const { q, trades, sectors, classifications } = router.query;
  const queryLocation = router.query.location;
  const workingLocation = queryLocation?.toString();
  const workingQ = q?.toString();
  const [paginationData, setPaginationData] = useState(initialPaginationData);
  const [isServerSideRender, setIsServerSideRender] = useState(true);
  const { filters, ...filterHandlers } = useFilters(
    initialFilters,
    router,
    setPaginationData,
    true
  );
  usePageView('ProjectSearch', {
    Country: geoInfo?.country?.alpha3,
    State: geoInfo?.state?.name,
    'State Abbreviation': geoInfo?.state?.abbreviation,
    City: geoInfo?.city,
    ...(workingQ || trades
      ? {
          'Search Text': workingQ || trades,
          'Search Type': trades ? 'Trade' : 'Sector',
        }
      : {}),
    'Results Count': initialResults?.count,
    'Initial Trades': trades,
    'Initial Sectors': sectors,
    'Initial Classifications': classifications,
  });

  useEffect(() => {
    setIsServerSideRender(false);
  }, []);

  const { data, isLoading } = useQuery(
    [
      'projectSearch',
      geoInfo,
      workingQ,
      filters,
      paginationData.page,
      paginationData.pageSize,
    ],
    async () => {
      if (isServerSideRender) {
        return initialResults;
      }
      if (!workingLocation || geoInfo?.country) {
        return fetchSearch(
          geoInfo,
          workingQ,
          filters,
          paginationData,
          !filters.radius.disabled && Number(filters.radius.value)
        );
      }

      return {
        results: [],
        count: 0,
      };
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  );

  if (dataError) {
    return (
      <LocationError
        title="Something Went Wrong"
        message={
          "We weren't able to complete your search. Please try searching again."
        }
        promptRetry
      />
    );
  }

  if (workingLocation && !geoInfo?.country) {
    return (
      <LocationError
        title="No Locations Match Your Search"
        message="Check your spelling and try searching again."
        states={topStates}
      />
    );
  }

  const breadcrumbs = getLocationBreadcrumbs(geoInfo, null, null, true);
  const workingLocationDescription = workingLocation
    ? ` ${I18n.t('pages.projects_search.in')} ${workingLocation}`
    : '';

  const pageTitle = I18n.t('pages.projects_search.title', {
    location: workingLocationDescription,
  });
  const pageDescription = I18n.t('pages.projects_search.description', {
    location: workingLocationDescription,
  });
  const pageUrl = `${origin}${router.asPath}`;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index,follow" />
      </Head>
      <main>
        <GoogleWebPageScript
          name={pageTitle}
          url={pageUrl}
          description={pageDescription}
        />
        <GoogleBreadcrumbScript breadcrumbs={breadcrumbs} origin={origin} />
        <Header />
        <Box>
          <SearchResults
            searchResults={(data || initialResults).results}
            geoInfo={geoInfo}
            title={`${I18n.t(
              'pages.projects_search.find_projects'
            )}${workingLocationDescription}`}
            breadcrumbs={breadcrumbs}
            count={(data || initialResults).count}
            useFilterHandlers={{ filters, ...filterHandlers }}
            setPaginationData={setPaginationData}
            paginationData={paginationData}
            isLoadingResults={isServerSideRender ? false : isLoading}
            filtersLists={filtersLists}
            showSimilarResults={false}
            projectsSearch
          />
        </Box>
        <Box className={classes.faqContainer}>
          <FrequentlyAskedQuestions faqs={faqs} />
        </Box>
      </main>
      <Footer />
    </>
  );
}
